const { fillRun } = require('./fillRun');
const { baysForRun } = require('./bays');
const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_FRONT_OVERHANG,
  KITCHEN_SEATING_OVERHANG,
} = require('../kitchen-assembly/constants');
const { wallParamToSurfacePoint } = require('../kitchen-assembly/wallMath');
const {
  applyAppearanceBundle,
  effectiveAppearance,
} = require('./bayOverrides');
const { markCountertopFreestanding } = require('./countertopExposure');
const { solidOf, solidsAhead, clearAlong } = require('./neighbourRuns');

const HEIGHT = 0.9144; // 36" — virtual wall extrusion height (uppers ref)
const INCHES_TO_METERS = 0.0254;
const STANDARD_CABINET_DEPTH = 24 * INCHES_TO_METERS;
const MIN_BACK_CABINET_DEPTH = 6 * INCHES_TO_METERS;
// The slack every depth boundary below is compared with. The generator's
// island depths (30", 36", 42") land exactly ON those boundaries, and the
// same 42" reached by different arithmetic is 1.0667999999999997 m — which
// lost its back row by a ten-quadrillionth of a metre.
const DEPTH_TOLERANCE = 1e-6;
const ISLAND_BACK_PANEL_THICKNESS = 1 * INCHES_TO_METERS;
/**
 * The default depth of a single-sided leg — a standard 24" row plus the 1"
 * finished back. The same number `islandPlacement.ISLAND_DEFAULT_DEPTH` sweeps
 * down to, kept here so a HAND-DRAWN leg (which stores no depth at all) and a
 * generated one describe the same island.
 */
const ISLAND_DEFAULT_DEPTH =
  STANDARD_CABINET_DEPTH + ISLAND_BACK_PANEL_THICKNESS;
/**
 * The shallowest a single row may be squeezed to in order to afford its
 * finished back. An unfinished back is a defect the room sees; a row an inch
 * under standard is merely a narrower box, so on a leg someone set to 24" the
 * panel wins and the row comes out 23". Below the shallowest cabinet the
 * engine builds at all there is nothing left to spend, and the leg goes
 * without.
 */
const MIN_SINGLE_ROW_DEPTH = MIN_BACK_CABINET_DEPTH;
// Knee space a seated edge claims out of the island's own depth. Same
// cantilever the counter always had — the difference is where it comes
// from (see `frontBackDepths`).
const SEATING_KNEE_DEPTH = 12 * INCHES_TO_METERS;
// Below this an island cannot be both seated and useful: the front row
// would have to give up part of its standard 24" to make knee space.
const MIN_SEATED_ISLAND_DEPTH = STANDARD_CABINET_DEPTH + SEATING_KNEE_DEPTH;

/** leftRot(dir) = (-dz, dx) — the engine's inward-normal convention. */
function inwardNormal(dir) {
  const len = Math.hypot(dir.x, dir.z) || 1;
  return { x: -dir.z / len, z: dir.x / len };
}

/**
 * Engine wall from a drawn backing line `start`→`end`. Orients bottomLeft→
 * bottomRight so the inward normal (where cabinets face) points to `side`
 * ('left'|'right' of the start→end travel direction). `inset` (default 0)
 * shifts the wall line toward the cook side, reserving the outer strip for a
 * finished back panel (generator passes 1"; manual passes 0).
 */
function synthesizeVirtualWall(
  start,
  end,
  { side = 'right', inset = 0, id = 'freestanding' } = {}
) {
  const dir = { x: end.x - start.x, z: end.z - start.z };
  const rightInward = inwardNormal(dir); // for start->end
  const wantPos = side === 'right'; // +Z-ish
  const flip = rightInward.z < 0 === wantPos;
  const bl0 = flip ? end : start;
  const br0 = flip ? start : end;
  const dir2 = { x: br0.x - bl0.x, z: br0.z - bl0.z };
  const n = inwardNormal(dir2);
  const bl = { x: bl0.x + n.x * inset, z: bl0.z + n.z * inset };
  const br = { x: br0.x + n.x * inset, z: br0.z + n.z * inset };
  return {
    id,
    alignment: {
      bottomLeft: { x: bl.x, y: 0, z: bl.z },
      bottomRight: { x: br.x, y: 0, z: br.z },
      topLeft: { x: bl.x, y: HEIGHT, z: bl.z },
      topRight: { x: br.x, y: HEIGHT, z: br.z },
    },
    isVirtual: true,
  };
}

/**
 * The finished back panel a SINGLE-sided leg spends out of its own depth.
 *
 * A single row's back is what the room sees, so it is always covered — but
 * the cover is part of the island's stated depth, not an inch bolted onto it:
 * the default 25" leg is a standard 24" cabinet plus this 1". Where a
 * producer MEASURED the row (`frontDepth`), that number wins and the panel is
 * whatever the depth leaves, capped at the standard thickness; where the leg
 * is too shallow to spend an inch and still hold a cabinet, it spends what it
 * can.
 */
function singleSidedBackPanelDepth(totalDepth, frontDepthOverride) {
  const spare =
    frontDepthOverride != null
      ? totalDepth - frontDepthOverride
      : totalDepth - MIN_SINGLE_ROW_DEPTH;
  return Math.max(0, Math.min(ISLAND_BACK_PANEL_THICKNESS, spare));
}

/**
 * Whether a SEATED island is deep enough to carve the diners' knee out of its
 * own depth, rather than cantilever the counter past its back. The one test
 * `frontBackDepths` builds by and the seating clearance (`barstoolSeating`)
 * grades by.
 */
function carvesSeatingKnee(islandDepth) {
  return islandDepth >= MIN_SEATED_ISLAND_DEPTH - DEPTH_TOLERANCE;
}

/**
 * Decide how to divide an island's depth into a front (working-side)
 * cabinet, an optional back-side cabinet, and an optional 1" back
 * cover panel:
 *   - 30"+ island:     front 24" + back (depth − 24")
 *   - 25"-29" island:  front 24" + thin back cover panel
 *   - < 25" island:    single full-depth cabinet
 */
function frontBackDepths(islandDepth, { seated = false } = {}) {
  // A seated edge claims its knee space FROM the island's depth rather
  // than cantilevering past it. The user picks which the back of the
  // island is for:
  //
  //   36"  seated: 24" cabs + 12" knee        storage: 24" cabs + 12" cabs
  //   48"  seated: 24" cabs + 12" cabs + 12" knee   storage: 24" + 24" cabs
  //
  // Two things follow. The back row shrinks by the knee rather than
  // vanishing, so a deep island keeps storage AND seats people — today
  // it kept the full back row and put stools against a cabinet face.
  // And because the counter no longer reaches into the aisle, the floor
  // beyond the island owes the diner only their 36" clearance, not
  // 12" + 36" (see SEATING_DEPTH_REQUIRED / seatedIslandFreeDepthNeeded).
  //
  // Below MIN_SEATED_ISLAND_DEPTH there is nothing to carve — the front
  // row would have to give up part of its 24" — so a shallow island
  // keeps the old cantilever and is unaffected.
  if (seated && carvesSeatingKnee(islandDepth)) {
    const usable = islandDepth - SEATING_KNEE_DEPTH;
    const back = usable - STANDARD_CABINET_DEPTH;
    return {
      frontDepth: STANDARD_CABINET_DEPTH,
      backDepth: back >= MIN_BACK_CABINET_DEPTH - DEPTH_TOLERANCE ? back : 0,
      backPanelThickness: 0,
      kneeDepth: SEATING_KNEE_DEPTH,
    };
  }
  if (
    islandDepth >=
    STANDARD_CABINET_DEPTH + MIN_BACK_CABINET_DEPTH - DEPTH_TOLERANCE
  ) {
    return {
      frontDepth: STANDARD_CABINET_DEPTH,
      backDepth: islandDepth - STANDARD_CABINET_DEPTH,
      backPanelThickness: 0,
      kneeDepth: 0,
    };
  }
  if (islandDepth >= STANDARD_CABINET_DEPTH + ISLAND_BACK_PANEL_THICKNESS / 2) {
    return {
      frontDepth: STANDARD_CABINET_DEPTH,
      backDepth: 0,
      backPanelThickness: islandDepth - STANDARD_CABINET_DEPTH,
      kneeDepth: 0,
    };
  }
  return {
    frontDepth: islandDepth,
    backDepth: 0,
    backPanelThickness: 0,
    kneeDepth: 0,
  };
}

/**
 * The depths a free-standing segment resolves to — the ONE answer the fill
 * builds from and the island panel displays.
 *
 * It exists because there were two. The fill's fallbacks for an unstored
 * `frontDepth` / `backDepth` are "nobody measured this, split the depth"
 * (`frontBackDepths`), and the panel's were "there is none" — so a
 * double-sided leg with neither stored SHOWED `Side A 36" / Side B 0"` while
 * the engine BUILT a 24" row and a 12" one. The panel commits a field only
 * when its text differs from what it displayed, so typing the 0 it was
 * already showing wrote nothing and the phantom row stayed. Two sets of
 * defaults for one question is the defect; this is the single set.
 *
 * Everything is measured from the run line (the datum the plan draws), and
 * the four numbers spend the island's `depth` exactly once:
 *
 *   Side A   `front`                       the working row
 *   Side B   `back` + `backPanel` + `knee` the second row (or its cover),
 *                                          then whatever is left over
 *
 * `back` is the depth of a back CABINET ROW and is 0 when the back is not one
 * — a back under `MIN_BACK_CABINET_DEPTH` is a finished panel of that depth,
 * which is `backPanel`'s job. Keeping them apart is what lets the caller add
 * the two without double-counting the sub-cabinet case.
 *
 * @param {Object} segment - the stored free-standing segment
 * @returns {{ sided, depth, front, back, backPanel, knee }} all in metres
 */
function resolveSegmentDepths(segment = {}) {
  const sided = segment.sided === 'double' ? 'double' : 'single';
  const frontOverride = Number.isFinite(segment.frontDepth)
    ? segment.frontDepth
    : null;
  const backOverride = Number.isFinite(segment.backDepth)
    ? segment.backDepth
    : null;
  // A leg with no stored depth is either a hand-drawn one (the default 25"
  // island) or a double-sided one whose two rows were measured and whose
  // total never was — in which case the rows ARE the total.
  const depth = Number.isFinite(segment.depth)
    ? segment.depth
    : sided === 'double' && frontOverride != null && backOverride != null
      ? frontOverride + backOverride
      : ISLAND_DEFAULT_DEPTH;

  if (sided === 'single') {
    const backPanel = singleSidedBackPanelDepth(depth, frontOverride);
    const front = Math.min(frontOverride ?? depth - backPanel, depth);
    return {
      sided,
      depth,
      front,
      back: 0,
      backPanel,
      knee: Math.max(0, depth - front - backPanel),
    };
  }

  const split = frontBackDepths(depth);
  const front = frontOverride ?? split.frontDepth;
  const stored = backOverride ?? split.backDepth;
  // A back row only exists where a CABINET fits. Anything shallower is a
  // finished panel of that depth — enter 3/4" and you get a 3/4" panel, not a
  // 3/4" cabinet — and a back of zero is finished at the standard thickness,
  // so a double-sided leg is never left with its cabinet backs facing the
  // room.
  const backIsRow = stored >= MIN_BACK_CABINET_DEPTH - DEPTH_TOLERANCE;
  const back = backIsRow ? stored : 0;
  const backPanel = backIsRow
    ? 0
    : stored > 0
      ? stored
      : split.backPanelThickness || ISLAND_BACK_PANEL_THICKNESS;
  return {
    sided,
    depth,
    front,
    back,
    backPanel,
    knee: Math.max(0, depth - front - back - backPanel),
  };
}

/**
 * A wall's own frame: the direction along it, and the two normals. Three
 * copies of this arithmetic had grown up in this file (the countertop
 * extension, the back panel, and now the seating), and a sign error in any
 * one of them puts geometry on the wrong face of the island.
 *
 * `inward` is where the cabinets face (the engine's convention, and Side A on
 * a free-standing leg); `outward` is Side B.
 */
function wallFrame(wall) {
  const bl = wall.alignment.bottomLeft;
  const br = wall.alignment.bottomRight;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const len = Math.hypot(dx, dz) || 1;
  const dir = { x: dx / len, z: dz / len };
  const inward = { x: -dir.z, z: dir.x };
  return { bl, br, len, dir, inward, outward: { x: -inward.x, z: -inward.z } };
}

/**
 * Flatten a fillRun result into a flat array of objects (all output arrays).
 */
function flattenFillResult(result) {
  return [
    ...result.cabinets,
    ...result.fillers,
    ...result.cornerUnits,
    ...result.countertops,
    ...result.appliancePlacements,
    ...(result.microwaves || []),
  ];
}

/**
 * Build a back-panel object for a freestanding segment. The panel sits on
 * the outward/seating side of the wall line, flush behind the cabinets.
 *
 * @param {Object} wall - Virtual wall (from synthesizeVirtualWall).
 * @param {number} lineLength - Total length of the segment in meters.
 * @param {number} floorY - Floor Y in world meters.
 * @param {number} panelThickness - Thickness in meters (1" = 0.0254 m).
 * @returns {Object} Blank-panel object.
 */
function buildBackPanel(wall, lineLength, floorY, panelThickness, appearance) {
  // The panel goes on the OUTWARD side of the wall: opposite the inward normal.
  // The cabinet's back face is flush with the wall surface (wall line).
  // So the panel center is shifted OUTWARD by panelThickness/2.
  const { bl, dir: wallDir, outward } = wallFrame(wall);

  const cabinetHeight = KITCHEN_CABINET_DIMENSIONS.height;
  // Orient the panel so its local +Z — its finished face — faces OUTWARD
  // toward the seating side.
  const wallRotationY = Math.atan2(outward.x, outward.z);

  // Mid-point along the wall axis
  const midX = bl.x + (lineLength / 2) * wallDir.x;
  const midZ = bl.z + (lineLength / 2) * wallDir.z;

  const panel = {
    id: `back_panel_${wall.id}`,
    type: 'blank-panel',
    label: 'Cabinet Back Panel',
    category: { 'blank-panel': true, freestanding: true, backPanel: true },
    center: {
      x: midX + (panelThickness / 2) * outward.x,
      y: floorY + cabinetHeight / 2,
      z: midZ + (panelThickness / 2) * outward.z,
    },
    dimensions: {
      x: lineLength,
      y: cabinetHeight,
      z: panelThickness,
      // A flat finished sheet: a back is priced and sold as sheet (Venus's
      // back-panel sheet, a generic panel), never as doors, so it is drawn
      // with none (the renderer no longer reads `decorativeFront`).
      freestanding: true,
    },
    rotation: { x: 0, y: wallRotationY, z: 0 },
    addedBy: 'manual-cabinet-run',
    lastModified: new Date().toISOString(),
  };
  // Match the front cabinets' finish (paintColor / texture) so the island
  // looks the same from both sides.
  applyAppearanceBundle(panel, appearance);
  return panel;
}

/**
 * Barstools for a leg whose Side B the user marked as seated.
 *
 * The stools stand off the outer face of everything solid on Side B (the back
 * row, or the finished panel where there is no row) and reach in under the
 * knee the depth leaves. That knee is the whole gate: without one the counter
 * ends flush with the cabinetry and a stool would be parked against a cabinet
 * face, which is the arrangement `frontBackDepths` was taught to avoid in the
 * first place. Below the standard `KITCHEN_SEATING_OVERHANG` the stools are
 * still emitted — a shallow knee is a choice, not an error — and warned about,
 * because nothing else on screen says the diner is short of room.
 *
 * Deliberately NOT gated on `cookSide` being right. It is a guess on 11 of the
 * corpus's 13 legs, so seating is where the user SAID it is; Side B is simply
 * the face the knee is on, which the depth model guarantees.
 *
 * Emitted into the run's own objects rather than alongside the appliances: the
 * run builds these the way it builds its back panel, so the sweep replaces
 * them on every re-solve instead of accumulating a fresh set.
 *
 * @param {Object} args
 * @param {Object} args.wall     - the front virtual wall (inward = Side A)
 * @param {number} args.lineLength
 * @param {number} args.floorY
 * @param {{back:number, backPanel:number, knee:number}} args.depths
 * @param {string} args.idPrefix
 * @returns {{ objects: Object[], warnings: Object[] }}
 */
function buildSegmentSeating({ wall, lineLength, floorY, depths, idPrefix }) {
  // Lazy on purpose: `barstoolSeating` destructures `carvesSeatingKnee`
  // out of THIS module at load time, so a top-level require here is a cycle —
  // whichever of the two loads first hands the other a half-built exports
  // object, and the function silently becomes `undefined`. Requiring inside
  // the call means both modules are finished by the time either is read.
  const {
    buildBarstoolObjects,
    edgeSeatsEnough,
  } = require('../kitchen-assembly/barstoolSeating');

  const warnings = [];
  const { back, backPanel, knee } = depths;

  if (!(knee > 1e-6)) {
    warnings.push({
      severity: 'warning',
      type: 'seating',
      message:
        'Side B is marked as seated but the rows use the whole depth — no knee space, so no stools. Make the island deeper or a row shallower.',
    });
    return { objects: [], warnings };
  }
  if (!edgeSeatsEnough(lineLength)) {
    warnings.push({
      severity: 'warning',
      type: 'seating',
      message:
        'Side B is marked as seated but the leg is too short to seat two people, so no stools.',
    });
    return { objects: [], warnings };
  }
  if (knee < KITCHEN_SEATING_OVERHANG - 1e-6) {
    warnings.push({
      severity: 'warning',
      type: 'seating',
      message: `Knee space is ${(knee / INCHES_TO_METERS).toFixed(1)}" — under the standard ${Math.round(
        KITCHEN_SEATING_OVERHANG / INCHES_TO_METERS
      )}". The stools will sit tight.`,
    });
  }

  const { bl, dir, outward } = wallFrame(wall);
  // The face the diner's knees meet: past the back row and its cover.
  const solidB = back + backPanel;
  const mid = {
    x: bl.x + (lineLength / 2) * dir.x + solidB * outward.x,
    z: bl.z + (lineLength / 2) * dir.z + solidB * outward.z,
  };

  return {
    objects: buildBarstoolObjects({
      faceCenter: mid,
      outward,
      along: dir,
      length: lineLength,
      floorY,
      idPrefix,
      // The knee the island actually carved, not the standard 12" — the stool
      // tucks under the lip it has.
      overhang: knee,
      addedBy: 'manual-cabinet-run',
    }),
    warnings,
  };
}

/**
 * Extend a countertop ALONG the segment's line — `startExtra` past the line's
 * start, `endExtra` past its end.
 *
 * The twin of `extendCountertopOutward`, for the axis it cannot reach. A seated
 * face on one of the island's LONG sides is an overhang across the line and
 * `segment.depth` carries it; a stool at an END of the run is an overhang along
 * the line, and until this existed the run spec had nowhere to put it. Rule
 * 9.12b opens exactly that by pulling the rows back, so the rebuild threw the
 * knee space away and left the island 12" shorter than the scan measured it
 * (26767201).
 *
 * The shift is taken in WORLD terms off the segment's own direction rather than
 * the virtual wall's: the back row's wall is synthesized reversed so its normal
 * faces the other way, so `start` means opposite ends of the two walls while it
 * means one thing on the line. Widening `dimensions.x` is sign-agnostic either
 * way — the slab is built against a wall laid on that line, so its local +X
 * runs along it.
 *
 * @param {Object} countertop - Countertop object from fillRun.
 * @param {{x:number,z:number}} dir - Unit vector, segment start → end.
 * @param {number} startExtra - Metres past the start (0 when there is none).
 * @param {number} endExtra - Metres past the end.
 * @returns {Object} New countertop, or the same one when there is no overhang.
 */
function extendCountertopAlong(countertop, dir, startExtra = 0, endExtra = 0) {
  const extra = (startExtra || 0) + (endExtra || 0);
  if (!(extra > 0)) return countertop;
  const shift = ((endExtra || 0) - (startExtra || 0)) / 2;
  // Which way the slab's own along-wall axis (`wallAxis`, i.e. `(cos, -sin)`)
  // runs relative to the line. `resyncCountertopsToCabinets` measures spans in
  // that frame, so the record it reads has to be in it too.
  const ry = countertop.rotation?.y ?? 0;
  const forward = Math.cos(ry) * dir.x - Math.sin(ry) * dir.z >= 0;
  return {
    ...countertop,
    dimensions: {
      ...countertop.dimensions,
      x: countertop.dimensions.x + extra,
    },
    center: {
      ...countertop.center,
      x: countertop.center.x + shift * dir.x,
      z: countertop.center.z + shift * dir.z,
    },
    // Declared, because the slab now deliberately reaches past its cabinets and
    // every pass that squares a counter back onto them would otherwise take it
    // straight off again — rule 10d did exactly that on 26767201, logging the
    // 12" it had just been given as slack ([8.7, 56.0]" -> [8.7, 44.0]").
    seatingOverhang: forward
      ? { low: startExtra || 0, high: endExtra || 0 }
      : { low: endExtra || 0, high: startExtra || 0 },
  };
}

/**
 * Extend a countertop by `extraDepth` on the outward side of a virtual wall.
 * The outward side is the seating/open side (opposite the inward normal).
 *
 * @param {Object} countertop - Countertop object from fillRun.
 * @param {Object} wall - Virtual wall whose outward direction to use.
 * @param {number} extraDepth - Additional depth in meters (12" = 0.3048 m).
 * @returns {Object} New countertop object with extended depth + shifted center.
 */
function extendCountertopOutward(countertop, wall, extraDepth) {
  const { outward } = wallFrame(wall);

  const halfExtra = extraDepth / 2;
  return {
    ...countertop,
    dimensions: {
      ...countertop.dimensions,
      z: countertop.dimensions.z + extraDepth,
    },
    center: {
      ...countertop.center,
      x: countertop.center.x + halfExtra * outward.x,
      z: countertop.center.z + halfExtra * outward.z,
    },
  };
}

/**
 * Where a run's segments END, in world space — every segment's two endpoints,
 * so a free-standing leg can tell whether one of its own ends is JOINED to a
 * neighbour or stands open. A wall-backed sibling's endpoints are projected
 * off its wall; a free-standing one carries them already.
 *
 * `skip` is the leg asking. Excluded by its ENDPOINTS, not by identity:
 * `materializeRun` hands `fillFreestandingSegment` a spread copy
 * (`{...seg, sided, cookSide}`), so an identity test never matches, the leg
 * finds its own two ends in the sibling list, both ends read as joined, and
 * the reveal is silently suppressed on the very path the app uses. The scan
 * path passes no siblings at all, so it kept working — which is exactly how
 * this survived a green suite and a corpus A/B.
 */
function runEndpointsExcept(segments, skip) {
  const isSelf = (s) =>
    s === skip ||
    (s.freestanding &&
      skip?.start &&
      skip?.end &&
      s.start &&
      s.end &&
      ((pointsClose(s.start, skip.start) && pointsClose(s.end, skip.end)) ||
        (pointsClose(s.start, skip.end) && pointsClose(s.end, skip.start))));
  const out = [];
  for (const s of segments || []) {
    if (!s || isSelf(s)) continue;
    if (s.freestanding) {
      if (s.start) out.push(s.start);
      if (s.end) out.push(s.end);
      continue;
    }
    if (!s.wall) continue;
    const a = wallParamToSurfacePoint(s.wall, s.startParam);
    const b = wallParamToSurfacePoint(s.wall, s.endParam);
    if (a) out.push(a);
    if (b) out.push(b);
  }
  return out;
}

/**
 * The countertop overhang past each END of a free-standing leg, along its own
 * line.
 *
 * An OPEN end gets the standard `KITCHEN_COUNTERTOP_FRONT_OVERHANG` reveal —
 * the same inch the front and back edges get, and the same one `buildIsland`
 * has always emitted (`dimensions.x = length + 2 * overhang`). The run engine
 * used to emit none, so every island the engine re-solved lost an inch off
 * each end: a visible step where the counter had overhung, and — because the
 * waterfall leg is placed INSIDE the slab footprint on an island — the leg's
 * outer face landed exactly on the end cabinet's end face. Two coplanar
 * surfaces facing the same way is Z-fighting, which is how this was found.
 *
 * It is also what makes the round trip stable. `autoIslands.dividerLine`
 * recovers the run length by SUBTRACTING those two inches from the top, so a
 * fill that does not put them back shortens the island by 2" on every
 * derive → fill cycle (98" -> 96" -> 94" measured). Nothing re-derives from
 * engine output today — the fill's cabinets are tagged `manual-cabinet-run`
 * and `groupIslandObjects` skips them — so the ratchet is latent rather than
 * live, but it is one re-tag away.
 *
 * A JOINED end gets nothing: an L-island's inside corner and a peninsula arm
 * meeting its wall run have cabinetry on the other side, and an inch of slab
 * there would push into it. An end that meets ANOTHER run is the same case,
 * but only the room knows it — see `revealsClearOfNeighbours`.
 *
 * The stored `startOverhang` / `endOverhang` are a scan-measured seating
 * cantilever along the axis, and they are TOTALS — `scanIslands.endOverhangs`
 * measures how far the top reaches past the line's end, which already includes
 * any reveal. So the reveal is a floor under them, never a term added to them.
 */
function endRevealsFor(segment, ctx, startOverhang, endOverhang) {
  const others = runEndpointsExcept(ctx?.run?.segments, segment);
  const open = (pt) => !others.some((o) => pointsClose(pt, o));
  const reveal = KITCHEN_COUNTERTOP_FRONT_OVERHANG;
  return {
    start:
      segment.start && open(segment.start)
        ? Math.max(reveal, startOverhang)
        : startOverhang,
    end:
      segment.end && open(segment.end)
        ? Math.max(reveal, endOverhang)
        : endOverhang,
  };
}

/**
 * The lip at each end of one countertop, cut back to the room another run
 * leaves ahead of it.
 *
 * `endRevealsFor` can only see the island's own run. An end butted against a
 * DIFFERENT run — a scanned island stretched flush into the wall run, or one
 * the scan already measured touching a run — read as open, so every rebuild
 * put the 1" lip an inch into that run's counter or cabinet. The slab now
 * slides out along the line until it meets what `ctx.neighbours` stands at its
 * own height (`neighbourRuns`), and the lip is whatever of the inch that
 * leaves: none against a run, the gap where the gap is narrower.
 *
 * A scan-measured overhang (`measured`) is kept as it stands: it is what the
 * scan saw, and the drag already stops the measured slab at the neighbour.
 */
function revealsClearOfNeighbours(countertop, reveals, a) {
  const { segment, lineDir, neighbours, measured } = a;
  if (!neighbours?.length) return reveals;
  const slab = solidOf(countertop);
  const room = (point, dir) =>
    point
      ? clearAlong(slab, dir, solidsAhead(neighbours, point, dir))
      : Infinity;
  const back = { x: -lineDir.x, z: -lineDir.z };
  return {
    start: Math.max(
      measured.start,
      Math.min(reveals.start, room(segment.start, back))
    ),
    end: Math.max(
      measured.end,
      Math.min(reveals.end, room(segment.end, lineDir))
    ),
  };
}

/**
 * Fill a free-standing cabinet segment (peninsula / island) by running
 * the cabinet-run engine on synthesized virtual walls.
 *
 * @param {{
 *   freestanding: boolean,
 *   start: {x, z},
 *   end: {x, z},
 *   depth: number,
 *   sided: 'single'|'double',
 *   cookSide: 'left'|'right',
 * }} segment
 * @param {{
 *   floorY?: number,
 *   roomAppearance?: Object,
 *   openings?: Object[],
 *   walls?: Object[],
 *   run?: Object,
 *   neighbours?: Object[],  other runs' solids (`neighbourRuns.runSolidsByOwner`)
 * }} ctx
 * @returns {{ objects: Object[], applianceObjects: Object[], warnings: string[],
 *   zones: Object[], slidBays: Object[] }} `slidBays`: the solved bays the
 *   dishwasher's end piece slid, each with the virtual `wall` it is solved on
 */
function fillFreestandingSegment(segment, ctx = {}) {
  const {
    start,
    end,
    sided = 'single',
    cookSide = 'right',
    // Counter overhang ALONG the line, past each end. `depth` covers the
    // across-the-line overhang; these two cover the axis it cannot express —
    // see `extendCountertopAlong`.
    startOverhang = 0,
    endOverhang = 0,
  } = segment;
  // `depth` and the explicit per-row `frontDepth` / `backDepth` are read
  // through `resolveSegmentDepths` below rather than destructured here: the
  // fallbacks for an unstored row are the whole subtlety (a DRAWN island is
  // one number and the split is standard; a SCANNED one is two rows RoomPlan
  // measured separately — 19"+8", 27"+15" — and that asymmetry is a finding),
  // and they have to be the same fallbacks the panel shows.
  const floorY = ctx.floorY ?? 0;
  const runBase = ctx.run || {};
  const lineLength = Math.hypot(end.x - start.x, end.z - start.z);
  const lineDir = lineLength
    ? { x: (end.x - start.x) / lineLength, z: (end.z - start.z) / lineLength }
    : { x: 1, z: 0 };
  // The standard 1" reveal at each OPEN end, floored under any scan-measured
  // seating cantilever — see `endRevealsFor`.
  // Cut back again wherever another run stands closer than that — see
  // `revealsClearOfNeighbours`.
  const reveals = endRevealsFor(segment, ctx, startOverhang, endOverhang);
  const overhangAlong = (ct) => {
    const lip = revealsClearOfNeighbours(ct, reveals, {
      segment,
      lineDir,
      neighbours: ctx.neighbours,
      measured: { start: startOverhang, end: endOverhang },
    });
    return extendCountertopAlong(ct, lineDir, lip.start, lip.end);
  };

  const oppositeSide = cookSide === 'right' ? 'left' : 'right';
  // Virtual-wall id base — unique per segment so a multi-leg run's cabinets,
  // countertops, and back panels don't collide on ids (every leg otherwise
  // numbers its first piece `freestanding_0`). The caller passes the stored
  // segment index; the default keys on the segment's own start point so two
  // separate free-standing runs in one room never mint the same ids (a
  // `Date.now()` stamp used to hide that collision — see buildIsland.js for
  // the same centre-in-millimetres convention).
  const wallId =
    ctx.segmentId ||
    `freestanding_${Math.round(start.x * 1000)}_${Math.round(start.z * 1000)}`;

  // Side A's wall — the datum, oriented so its inward normal points at the
  // cook side. BOTH branches built this identically; it is hoisted because the
  // seating pass below needs the same frame and a second `synthesizeVirtualWall`
  // call is a second chance to get the side wrong.
  const wall = synthesizeVirtualWall(start, end, {
    side: cookSide,
    id: wallId,
  });
  // One answer for the row split, shared with the island panel — see
  // `resolveSegmentDepths`.
  const depths = resolveSegmentDepths(segment);

  const objects = [];
  // Appliance objects the engine emits from an anchor — a sink basin and its
  // faucet, a dishwasher, a range. Kept APART from the bodies because a
  // DERIVED run (auto / scan) must not re-emit them: whoever placed the
  // appliance still owns it, and the run only reserves its span. The
  // wall-backed branch of `materializeRunObjects` has always applied that
  // rule; the free-standing branch could not express it while this function
  // returned one flat array.
  const applianceObjects = [];
  const warnings = [];
  // The Side A row's fill zones (`fillRun`'s `zones`) — the stretch a bay
  // edge drag may grow into. Side B carries no bays, so it has none to give.
  let zones = [];
  // The Side A bays a dishwasher's end piece SLID (`fillRun` stamps
  // `endPieceSlide`), each with the virtual wall it was solved along. A derived
  // run never re-emits its appliances, so the caller moves the objects
  // (`moveApplianceObjectsToBays`), and that needs the wall the params are on.
  let slidBays = [];
  const slidOn = (fill, onWall) =>
    (fill.bays || [])
      .filter((b) => b.endPieceSlide != null)
      .map((b) => ({ ...b, wall: onWall }));
  // Finish for the seating-side back panel — the same effective bundle the
  // front cabinets get (run ?? room), so both sides of the island match.
  const backAppearance = effectiveAppearance({ run: runBase, ctx });

  if (sided === 'single') {
    // Single-sided: one cabinet row facing the cook side.
    //
    // The island's `depth` already CONTAINS whatever the seated edge takes:
    // `autoIslands` measures it off the COUNTERTOP, so a knee is inside it
    // whether the island carved one out of its own body (deep) or
    // cantilevered the slab past its face (shallow). So the ROW's depth is
    // `frontDepth` where a producer measured one, and the rest of the depth
    // is spent on Side B — see the slab below.
    //
    // Spending the total on the cabinet box is what made a 42" island's
    // cabinets swallow its own 18" knee, and its barstools with it.
    const totalDepth = depths.depth;
    // The panel comes OUT OF the depth, exactly as `frontBackDepths` spends a
    // 25-29" double-sided island (front 24" + thin cover). Where a producer
    // measured the row, that number wins and the panel is whatever is left.
    const backPanelDepth = depths.backPanel;
    const cabinetDepth = depths.front;
    // Spread the stored run, don't re-litter it. `fillRun` reads `family` (the
    // module widths a "Stock Modular" island must pack to) and `source` (a scan
    // run is never inset for an end panel, because the scan path leaves the
    // measured appliance exactly where it stands) straight off the run — and a
    // rebuilt literal carried neither, so a free-standing leg packed on the
    // generic 3" grid however the panel was set.
    const run = {
      ...runBase,
      id: runBase.id || 'freestanding',
      segments: [{ wall, startParam: 0, endParam: lineLength }],
      bays: baysForRun(runBase),
      bayOverrides: runBase.bayOverrides || [],
      appearance: runBase.appearance,
    };
    const result = fillRun(run, {
      floorY,
      ceilingY: ctx.ceilingY,
      ceilingId: ctx.ceilingId,
      roomAppearance: ctx.roomAppearance,
      openings: [],
      walls: [],
      cabinetDepth,
    });
    warnings.push(...result.warnings);
    zones = result.zones || [];
    slidBays = slidOn(result, wall);

    // One slab reaching the island's own outer edge, and past either END the
    // rows were pulled back from — the SAME expression as the double-sided
    // branch below, because it is the same island: `depth` is the whole body,
    // Side A spends `front` of it, and everything else belongs to Side B.
    // `fillRun` has already added the 1" reveal each side.
    //
    // There is deliberately NO seating term. This used to cantilever a flat
    // `KITCHEN_SEATING_OVERHANG` here on every single-sided leg, whether or
    // not anyone was seated: an island with no stools at all grew a 12"
    // cantilever. Removing that was right; sending what remained toward the
    // COOK side was not, and is what put an 11" knee over the cabinet doors
    // and left the finished back with nothing over it. `scanIslands` states
    // the convention the rest of the system follows — "cabinets face the
    // nearest cabinet run, seating goes on the side away" — so the leftover
    // is Side B's, and the panel, the plan's Side A/Side B labels and both
    // branches of this function now agree on that.
    //
    // Tagged free-standing: both long edges are exposed, so the renderer
    // profiles the back edge too (see countertopExposure.js).
    const beyondCabinets = Math.max(0, totalDepth - cabinetDepth);
    const extendedCountertops = result.countertops.map((ct) =>
      markCountertopFreestanding(
        overhangAlong(extendCountertopOutward(ct, wall, beyondCabinets))
      )
    );

    objects.push(
      ...result.cabinets,
      ...result.fillers,
      ...result.cornerUnits,
      ...(result.microwaves || []),
      ...extendedCountertops
    );
    applianceObjects.push(...result.appliancePlacements);

    // The finished back panel on the outward/seating side, inside the depth.
    if (backPanelDepth > 1e-6) {
      objects.push(
        buildBackPanel(wall, lineLength, floorY, backPanelDepth, backAppearance)
      );
    }
  } else {
    // Double-sided: front + back cabinet rows (or front + back panel).
    // `resolveSegmentDepths` owns the split, including the rule that a back
    // under `MIN_BACK_CABINET_DEPTH` is a finished PANEL of that depth rather
    // than a cabinet, and that a back of zero is covered at the standard
    // thickness — so a double-sided leg is never left with its cabinet backs
    // facing the room.
    const frontDepth = depths.front;
    const backDepth = depths.back;
    const backPanelThickness = depths.backPanel;
    const backIsRow = backDepth > 0;

    // Front pass (cook side).
    const frontWall = wall;
    const frontRun = {
      ...runBase,
      id: runBase.id || 'freestanding',
      segments: [{ wall: frontWall, startParam: 0, endParam: lineLength }],
      bays: baysForRun(runBase),
      bayOverrides: runBase.bayOverrides || [],
      appearance: runBase.appearance,
    };
    const frontResult = fillRun(frontRun, {
      floorY,
      ceilingY: ctx.ceilingY,
      ceilingId: ctx.ceilingId,
      roomAppearance: ctx.roomAppearance,
      openings: [],
      walls: [],
      cabinetDepth: frontDepth,
    });
    warnings.push(...frontResult.warnings);
    zones = frontResult.zones || [];
    slidBays = slidOn(frontResult, frontWall);

    let backResult = null;
    if (backIsRow) {
      // Back pass (seating/open side) — second cabinet row facing opposite.
      const backWall = synthesizeVirtualWall(start, end, {
        side: oppositeSide,
        id: `${wallId}_back`,
      });
      // The back row carries the island's family and source too — it is the same
      // island — but never its bays or overrides: those are anchored to the
      // front row's params and would place a second copy of every appliance.
      const backRun = {
        ...runBase,
        id: (runBase.id || 'freestanding') + '_back',
        segments: [{ wall: backWall, startParam: 0, endParam: lineLength }],
        bays: [],
        bayOverrides: [],
        appearance: runBase.appearance,
      };
      backResult = fillRun(backRun, {
        floorY,
        roomAppearance: ctx.roomAppearance,
        openings: [],
        walls: [],
        cabinetDepth: backDepth,
      });
      warnings.push(...backResult.warnings);
    }

    // Bodies from both rows. The countertops are handled TOGETHER below: the
    // two passes run against opposite virtual walls and each adds its own
    // overhang off the shared divider plane, so pushing both rows' slabs
    // leaves two of them OVERLAPPING BY 2" at the same height. An island's
    // counter is one surface — which is what `buildIsland` emits for the
    // generated form of the same island, and what the peninsula path achieves
    // with its own full-length overhang strip.
    const rowBodies = (r) => [
      ...r.cabinets,
      ...r.fillers,
      ...r.cornerUnits,
      ...(r.microwaves || []),
    ];
    objects.push(...rowBodies(frontResult));
    applianceObjects.push(...frontResult.appliancePlacements);
    if (backResult) {
      objects.push(...rowBodies(backResult));
      applianceObjects.push(...backResult.appliancePlacements);
    }

    // One slab spanning both rows. `segment.depth` is the island's TOTAL
    // depth, so this covers a seated island's knee with no seating special
    // case: the slab reaches the same outer edge `buildIsland` gives it, and
    // the barstools' counter edge does not move.
    //
    // No overhang term — `buildCountertopRuns` already extends a slab 1" past
    // a VIRTUAL wall (its back-panel cover allowance), and on this segment
    // that inch IS the back edge's overhang.
    const totalDepth = depths.depth;
    const outward = Math.max(0, totalDepth - frontDepth);
    // Fillers included: the front pass's own slab lists its filler among its
    // parents (a filler is a `lower-cabinet` and holds the counter up like any
    // other bay), so the back row's must be listed the same way or ownership
    // reads differently for the two halves of one island.
    const backParents = [
      ...(backResult?.cabinets || []),
      ...(backResult?.fillers || []),
    ].map((c) => ({ id: c.id, type: 'lower-cabinet' }));
    for (const ct of frontResult.countertops) {
      const spanning = markCountertopFreestanding(
        overhangAlong(extendCountertopOutward(ct, frontWall, outward))
      );
      objects.push(
        backParents.length
          ? { ...spanning, parents: [...(ct.parents || []), ...backParents] }
          : spanning
      );
    }

    if (!backResult && backPanelThickness > 0) {
      // Back panel instead of a second cabinet row.
      objects.push(
        buildBackPanel(
          frontWall,
          lineLength,
          floorY,
          backPanelThickness,
          backAppearance
        )
      );
    }
  }

  // Stools, when the user marked Side B as seated. After the branch because
  // the answer is the same for both: the knee is Side B's by construction, so
  // the only thing that differs is how much of Side B the rows already took —
  // which `depths` carries.
  if (segment.seated) {
    const seating = buildSegmentSeating({
      wall,
      lineLength,
      floorY,
      depths,
      idPrefix: wallId,
    });
    objects.push(...seating.objects);
    warnings.push(...seating.warnings);
  }

  return { objects, applianceObjects, warnings, zones, slidBays };
}

/**
 * Return true when two points are within 1 mm of each other.
 * Used for connectivity checks between free-standing segment endpoints.
 */
function pointsClose(a, b) {
  return Math.hypot(a.x - b.x, a.z - b.z) < 1e-3;
}

/**
 * Which free-standing leg an object belongs to, or null if it belongs to none.
 *
 * `freeSegIndex` is stamped by `materializeRun` onto objects IT emits. An
 * island DERIVED into a run but never re-solved still holds the generator's
 * ORIGINAL objects: they carry `runId` and no tag. Every reader keyed on the
 * tag alone then reads them as wall-backed — the island panel is replaced by
 * the uppers toggle and a bay-override UI that does not apply, and neither
 * Move island nor the run datum appears. The run is the truth; the tag is a
 * shortcut, so fall back to it (found 2026-09-07 on a kitchen whose island
 * had never been re-solved).
 *
 * A single-leg run answers itself. A multi-leg run picks the nearest leg to
 * the object's own centre rather than guessing an index.
 */
function freeSegIndexOf(object, run) {
  if (object?.freeSegIndex != null) return object.freeSegIndex;
  const segs = run?.segments || [];
  const free = [];
  segs.forEach((s, i) => {
    if (s?.freestanding && s.start && s.end) free.push(i);
  });
  if (!free.length) return null;
  if (free.length === 1) return free[0];
  const c = object?.center;
  if (!c) return null;
  let best = null;
  let bestD = Infinity;
  for (const i of free) {
    const d = pointToSegmentDistance(c, segs[i].start, segs[i].end);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Distance from point `p` to segment `a`→`b` (2D, {x,z}). */
function pointToSegmentDistance(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  let t = len2 ? ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

const RUN_CLUSTER_GAP = 1.2; // meters — max centre gap within one cabinet run

/**
 * Cluster cabinet centres into runs by adjacency (union-find): two centres join
 * the same run when within `gap` of each other. Used to group cabinets that
 * carry no runId (e.g. generator output) so the cook-side guess can reason about
 * the nearest *run* rather than the nearest single cabinet.
 *
 * @param {Array<{x:number,z:number}>} centres
 * @param {number} [gap]
 * @returns {Array<Array<{x:number,z:number}>>}
 */
function clusterCentresIntoRuns(centres, gap = RUN_CLUSTER_GAP) {
  const n = centres.length;
  const parent = centres.map((_, i) => i);
  const find = (i) => {
    let r = i;
    while (parent[r] !== r) {
      parent[r] = parent[parent[r]];
      r = parent[r];
    }
    return r;
  };
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      if (
        Math.hypot(centres[i].x - centres[j].x, centres[i].z - centres[j].z) <=
        gap
      ) {
        parent[find(i)] = find(j);
      }
    }
  }
  const groups = new Map();
  for (let i = 0; i < n; i += 1) {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(centres[i]);
  }
  return [...groups.values()];
}

/**
 * Determine the default `sided` and `cookSide` for a free-standing segment
 * when the user has not made an explicit choice.
 *
 * - `sided`:    `'double'` when BOTH endpoints of `segment` connect to another
 *               free-standing segment in `freestandingSegments` (i.e. the leg
 *               is interior — in the middle of a floating U or L). Otherwise
 *               `'single'` (peninsula or standalone terminal leg).
 *
 * - `cookSide`: faces the **nearest cabinet run** — cabinets point toward the
 *               centroid of whichever run in `otherCabinetRuns` is closest to
 *               this segment. Callers pass only real runs (the scattered,
 *               individually-placed cabinets are filtered out upstream), so the
 *               nearest counter wins. Defaults to `'right'` when there are no
 *               runs.
 *
 * @param {{ freestanding: boolean, start: {x,z}, end: {x,z} }} segment
 * @param {Array<{ freestanding: boolean, start: {x,z}, end: {x,z} }>} freestandingSegments
 * @param {Array<Array<{x: number, z: number}>>} otherCabinetRuns - cabinet
 *   centres grouped into real runs (runId groups + contiguous ≥3 clusters).
 * @returns {{ sided: 'single'|'double', cookSide: 'left'|'right' }}
 */
function defaultSidedness(segment, freestandingSegments, otherCabinetRuns) {
  const { start, end } = segment;

  // --- sided: interior when BOTH endpoints connect to a DIFFERENT free leg ---
  const others = freestandingSegments.filter((s) => s !== segment);

  function isConnected(pt) {
    return others.some(
      (s) => pointsClose(pt, s.start) || pointsClose(pt, s.end)
    );
  }

  const interior = isConnected(start) && isConnected(end);
  const sided = interior ? 'double' : 'single';

  // --- cookSide: cabinets face the nearest run, seating on the side away ---
  const dir = { x: end.x - start.x, z: end.z - start.z };
  const rightInward = inwardNormal(dir); // engine convention: (-dz, dx) normalised
  // synthesizeVirtualWall maps side:'right' to the inward normal with z >= 0
  // (its "+Z-ish" convention), so we must test the run against THAT facing
  // direction — not raw rightInward — or the cook side inverts for islands drawn
  // so rightInward points -Z (e.g. drawn right-to-left).
  const facingRight =
    rightInward.z >= 0 ? rightInward : { x: -rightInward.x, z: -rightInward.z };
  const mid = { x: (start.x + end.x) / 2, z: (start.z + end.z) / 2 };

  let nearest = null;
  let nearestDist = Infinity;
  for (const run of otherCabinetRuns || []) {
    if (!run || !run.length) continue;
    let d = Infinity;
    for (const c of run) {
      d = Math.min(d, pointToSegmentDistance(c, start, end));
    }
    if (d < nearestDist) {
      nearestDist = d;
      nearest = run;
    }
  }

  let cookSide = 'right';
  if (nearest) {
    const cx = nearest.reduce((s, c) => s + c.x, 0) / nearest.length;
    const cz = nearest.reduce((s, c) => s + c.z, 0) / nearest.length;
    // Cabinets face the run (positive => +facingRight => side 'right').
    const dot = (cx - mid.x) * facingRight.x + (cz - mid.z) * facingRight.z;
    cookSide = dot >= 0 ? 'right' : 'left';
  }

  return { sided, cookSide };
}

module.exports = {
  ISLAND_BACK_PANEL_THICKNESS,
  MIN_SEATED_ISLAND_DEPTH,
  SEATING_KNEE_DEPTH,
  carvesSeatingKnee,
  synthesizeVirtualWall,
  frontBackDepths,
  singleSidedBackPanelDepth,
  resolveSegmentDepths,
  wallFrame,
  fillFreestandingSegment,
  defaultSidedness,
  clusterCentresIntoRuns,
  pointToSegmentDistance,
  freeSegIndexOf,
};
